import React, { useMemo, useRef } from "react";
import DatePickerComponent from "../UniversalComponents/DatePickerComponent";

import TimePickerComponent from "../UniversalComponents/TimePickerComponent";
import SelectComponent from "../UniversalComponents/SelectComponent";
import TextAreaComponent from "../UniversalComponents/TextAreaComponent";
import BasicSelect from "../Task/BasicSelect";
import { createPortal } from "react-dom";
import moment from "moment";
import { getFlushedDate, convertTimeToDateObj } from "../../utils/utilityFunctions/dateUtility";
import { flattenTree } from "../../utils/utilityFunctions/stringUtility";
import isEqual from "lodash/isEqual";
import AdvancedInputComponent from "../UniversalComponents/AdvancedInputComponent";

/**
 * Converts JS Date to 24h format time string
 */
const formatTime = (date) => date?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }) || "";

/**
 * Generate time options from 00:00:00 to 23:30:00
 */
const generateTimeOptions = () => {
  const times = [];
  for (let i = 0; i < 24; i++) {
    const hour = i.toString().padStart(2, "0");
    times.push(`${hour}:00:00`, `${hour}:30:00`);
  }
  return times;
};

/**
 * Main FieldElements component
 */
const AdvancedFieldElements = ({
  id,
  name,
  label,
  value,
  onChange,
  placeholder,
  type,
  size = 1,
  disabled,
  isFormDisabled,
  selectionList,
  formData = {},
  defaultOptions,
  dynamicOptions,
  apiCall,
  addButton,
  addButtonLabel,
  addButtonAction,
  isLoading,
  useFetchHook = () => [() => {}, {}],
  hookPayload,
  noKeyboardNavigation,
  noDisabled,
  displayValueKey,
  displayName,
  updateTags,
  clickTrigger,
  isNegativeValue = false,
  className = "",
  labelKey = "label",
  valueKey = "value",
  valueKeyBased,
  mandatory = false,
  ...props
}) => {
  const [fetchFieldValues, { data: fieldValueList, isFetching: isFieldFetching }] = useFetchHook() || [() => {}, {}];

  const options = useMemo(() => {
    if (defaultOptions) return defaultOptions;
    if (dynamicOptions) return dynamicOptions;

    return fieldValueList?.data?.map((item) =>
      valueKeyBased ? { label: item[labelKey], value: item[valueKey] } : { label: item, value: item }
    );
  }, [defaultOptions, dynamicOptions, fieldValueList, labelKey, valueKey, valueKeyBased]);

  const sharedProps = {
    id,
    name,
    value,
    disabled: (isFormDisabled || disabled) && !noDisabled,
    placeholder,
    mandatory,
    onChange,
  };

  const renderSelect = () => {
    const selectedOptions = flattenTree(options || [])?.filter((opt) =>
      isEqual(String(formData[name])?.toLowerCase(), String(opt?.value)?.toLowerCase())
    );

    return (
      <BasicSelect
        {...sharedProps}
        options={options || []}
        selectedOptions={selectedOptions}
        displayValue={formData[displayValueKey] ?? displayName}
        apiCall={apiCall}
        dynamicOptions={dynamicOptions}
        isLoading={isLoading || isFieldFetching}
        addButton={addButton}
        addButtonLabel={addButtonLabel}
        addButtonAction={addButtonAction}
        useFetchHook={useFetchHook}
        handleCallHook={hookPayload && (() => fetchFieldValues({ payload: hookPayload }, true))}
        showSearch={options?.length > 5}
        noKeyboardNavigation={noKeyboardNavigation}
        className={`form-select rounded border ${
          isFormDisabled || disabled ? "border-DFE2E5 bg-EDEDED text-868686" : "border-DFE2E5 bg-white text-434852"
        } focus:outline-hidden focus:ring-0 focus:border-1D5D9B active:border active:border-DFE2E5 text-xs block w-full h-7 py-0`}
      />
    );
  };

  const renderDate = () => (
    <div className="flex relative w-full custom-datepicker-container">
      <DatePickerComponent
        {...sharedProps}
        selected={getFlushedDate(formData[name])}
        value={formData?.[name] ? new Date(formData[name]) : null}
        onChange={(selectedDate) =>
          onChange({
            target: {
              name,
              value: selectedDate ? moment(selectedDate).format("YYYY-MM-DD") : null,
            },
          })
        }
        popperContainer={({ children }) => createPortal(children, document.getElementById("portal-root"))}
        dateFormat="dd-MM-yyyy"
        maxDate={new Date("2050-01-01")}
        minDate={new Date("2001-01-01")}
        showYearDropdown
        showMonthDropdown
        scrollableYearDropdown
        yearDropdownItemNumber={500}
        popperPlacement="bottom-end"
        size="small"
        className="custom-select bg-white rounded-sm w-full"
      />
    </div>
  );

  const renderTime = () => (
    <TimePickerComponent
      {...sharedProps}
      value={formData?.[name] ? convertTimeToDateObj(formData[name]) : null}
      onChange={(date) =>
        onChange({
          target: {
            name,
            value: formatTime(date),
          },
        })
      }
      popperContainer={({ children }) => createPortal(children, document.getElementById("portal-root"))}
      size="medium"
    />
  );

  const renderInput = () => (
    <AdvancedInputComponent {...sharedProps} type="text" inputClassName="h-[30px] text-xs" {...props} />
  );

  const renderNumber = () => (
    <AdvancedInputComponent
      {...sharedProps}
      type="number"
      inputClassName="h-[30px] text-xs"
      min={!isNegativeValue ? 0 : undefined}
      showArrow
    />
  );

  const renderTags = () => (
    <SelectComponent
      {...sharedProps}
      options={options}
      value={options?.filter((opt) => value?.includes(opt.value))}
      onChange={onChange}
      multiSelect
      clickTrigger={clickTrigger}
      style={{ width: "100%", height: "35px", padding: "5px", boxSizing: "border-box" }}
      isTagList
      size="small"
      editListHandler={(oldTag, newTag, e) => updateTags?.({ oldTag, newTag })}
    />
  );

  const renderTextArea = () => <TextAreaComponent {...sharedProps} className={`text-xs cursor-pointer ${className}`} />;

  const renderFile = () => (
    <input
      type="file"
      {...sharedProps}
      className={`rounded border text-xs block w-full ${
        isFormDisabled || disabled ? "border-DFE2E5 bg-EDEDED text-868686" : "border-DFE2E5 bg-white text-434852"
      } focus:outline-hidden focus:ring-0 focus:border-1D5D9B active:border active:border-DFE2E5`}
    />
  );

  const renderEmail = () => (
    <AdvancedInputComponent
      {...sharedProps}
      type="email"
      inputClassName="h-[30px] text-xs"
      autoComplete="email"
      placeholder={placeholder || "Enter email"}
      {...props}
    />
  );

  const renderPassword = () => (
    <AdvancedInputComponent
      {...sharedProps}
      type="password"
      inputClassName="h-[30px] text-xs"
      autoComplete="new-password"
      placeholder={placeholder || "Enter password"}
      {...props}
    />
  );

  const renderMultiSelect = () => (
    <SelectComponent
      {...sharedProps}
      value={options?.filter((opt) => value?.includes(opt.value))}
      options={options}
      onChange={onChange}
      multiSelect
      placeholder={placeholder || "Select one or more"}
      size="small"
      style={{
        width: "100%",
        height: "35px",
        padding: "5px",
        boxSizing: "border-box",
      }}
    />
  );

  switch (type) {
    case "select":
      return renderSelect();
    case "date":
      return renderDate();
    case "time":
      return renderTime();
    case "text":
      return renderInput();
    case "number":
      return renderNumber();
    case "hours":
      return renderNumber();
    case "tags":
      return renderTags();
    case "textArea":
      return renderTextArea();
    case "file":
      return renderFile();
    case "email":
      return renderEmail();

    case "password":
      return renderPassword();

    case "multiselect":
      return renderMultiSelect();
    default:
      return null;
  }
};

export default AdvancedFieldElements;
